<template>
  <div>
    <header-with-help-button v-if="helpButtonUrl || pageTitle" :title="pageTitle" :helpDocId="helpButtonUrl" />

    <p class="text-info" v-if="infoTitle">
      <i class="fa-duotone fa-square-info fa-lg mr-half"></i>
      <span v-html="infoTitle"></span>
    </p>
    <h4 class="text-info mb-1">
      <i>{{ infoDescription }}</i>
    </h4>

    <div v-if="isFetching">
      <c-spinner />
    </div>

    <ul  class="list-unstyled" :id="`${listName}-list`">
      <li class="ui-sortable-handle" v-for="item in items" :id="`${listName}-${item.id}`">
        <c-sortable-item @edit="openEditModal(item)" @remove="openRemoveConfirmModal(item)">
          <i :class="`fa-duotone fa-lg fa-fw ${listIcon}`"></i>
          {{ item[listItemName] }}
          <span v-if="listItemAdditionalField">({{ item[listItemAdditionalField] }})</span>
        </c-sortable-item>
      </li>
    </ul>

    <c-button
      type="button"
      theme="success"
      icon="fa-regular fa-plus"
      @click="openCreateModal"
    >
      Add {{ elementName }}
    </c-button>

    <c-confirm-delete-modal
      :is-open="showRemoveConfirmModal"
      @close="showRemoveConfirmModal=false"
      @confirm="remove"
      :show-indicator="showIndicator"
      :title="`Delete ${elementName}`"
      :description="`Are you sure you want to delete this ${elementName }?:`"
      :body="currentItem[listItemName]"
    />

    <c-modal
      v-if="showCreateEditModal"
      :size="modalSize"
      :title="`${currentItem.id ? 'Edit' : 'Add a New'} ${elementName}`"
      @close="showCreateEditModal = false"
    >
      <validation-observer v-slot="{ invalid, valid, errors, ...rest }" ref="form">
        <form @submit.prevent="submit">
          <div class="modal-body">
            <c-select
              v-if="templates && templates.length"
              :empty-option="true"
              empty-option-label="- Select -"
              label="Select Template"
              v-model="templateId"
              :options="templates">
            </c-select>

            <fragment v-for="(item, index) in fields" :key="`field-${index}`">
              <validation-provider
                :vid="item.name"
                :rules="item.isRequired && 'required'"
                v-slot="{ errors }"
              >
                <c-input
                  v-if="item.type === 'text'"
                  :label="item.label"
                  :error="errors[0]"
                  :required-mark="item.isRequired"
                  v-model="currentItem[item.name]"
                />

                <c-textarea
                  v-if="item.type === 'textarea'"
                  :label="item.label"
                  :error="errors[0]"
                  :required-mark="item.isRequired"
                  :rows="item.rows"
                  v-model="currentItem[item.name]"
                />

                <c-editor
                  :id="`editor-${index}`"
                  v-if="item.type === 'editor'"
                  :label="item.label"
                  :error="errors[0]"
                  :required-mark="item.isRequired"
                  v-model="currentItem[item.name]"
                />

                <c-checkbox
                    v-if="item.type === 'checkbox'"
                    :label="item.label"
                    :error="errors[0]"
                    v-model="currentItem[item.name]"
                />
              </validation-provider>
            </fragment>
          </div>
          <div class="modal-footer">
            <div>
              <c-button
                type="button"
                theme="default"
                icon="fa-remove"
                @click="closeCreateEditModal()"
              >
                Close
              </c-button>

              <div class="right-buttons-wrapper">
                <c-checkbox
                  class="mr-2"
                  v-if="showSaveAsTemplate"
                  v-model="currentItem.as_template"
                  label="Save as Template"
                />

                <c-button
                  type="submit"
                  theme="success"
                  icon="fa-save"
                  :show-indicator="showIndicator"
                >
                  Save
                </c-button>
              </div>
            </div>
          </div>
        </form>
      </validation-observer>
    </c-modal>
  </div>
</template>

<script>
import { fetchData } from '../../api/network'
import HeaderWithHelpButton from './HeaderWithHelpButton'

export default {
  props: {
    modalSize: {
      type: String,
      default: 'md'
    },
    pageTitle: {
      type: String,
      default: ''
    },
    infoTitle: {
      type: String,
      default: ''
    },
    infoDescription: {
      type: String,
      default: ''
    },
    listIcon: {
      type: String,
      default: 'fa-circle-dot text-primary'
    },
    showSaveAsTemplate: {
      type: Boolean,
      default: false
    },
    listName: {
      type: String,
      default: 'item'
    },
    listItemName: {
      type: String,
      default: 'name'
    },
    elementName: {
      type: String,
      default: 'item'
    },
    helpButtonUrl: {
      type: String,
      default: ''
    },
    templateDataByIdUrl: {
      type: String,
      default: ''
    },
    isFetching: {
      type: Boolean,
      default: false
    },
    items: {
      type: Array,
      default: () => []
    },
    templates: {
      type: Array,
      default: () => []
    },
    fields: {
      type: Array,
      default: () => []
    },
    showIndicator: {
      type: Boolean,
      default: false
    },
    listItemAdditionalField: {
      type: String,
      default: ''
    }
  },
  components: {
    HeaderWithHelpButton
  },
  data() {
    return {
      showCreateEditModal: false,
      showRemoveConfirmModal: false,
      currentItem: {},
      templateId: null
    }
  },
  watch: {
    'templateId': {
      handler(newVal) {
        const baseUrl = `${this.templateDataByIdUrl}/${newVal}`

        if (newVal) {
          fetchData(baseUrl).then((resp) => {
            const id = this.currentItem.id ? this.currentItem.id : null

            const data = { ...resp.data.fields }
            delete data.id
            this.currentItem = data

            this.fields.forEach((field, index) => {
              if (field.type === 'editor') {
                $(`#editor-${index}`).summernote('code', resp.data.fields[`${field.name}`])
              }
            })

            if (id) {
              this.currentItem.id = id
            }
          })
            .catch(() => {})
        }
      },
      deep: false
    }
  },
  methods: {
    resetCurrent() {
      this.currentItem = {}
      this.templateId = null
    },

    openCreateModal() {
      this.resetCurrent()
      this.showCreateEditModal = true
    },

    openEditModal(item) {
      this.currentItem = { ...item }
      this.showCreateEditModal = true
    },

    closeCreateEditModal() {
      this.showCreateEditModal = false
      this.resetCurrent()
    },

    submit() {
      this.$refs.form.validate().then(success => {
        if (!success) {
          return
        }

        if (this.currentItem.id) {
          this.$emit('edit', this.currentItem, this.$refs.form, this.closeCreateEditModal)
        } else {
          this.$emit('create', this.currentItem, this.$refs.form, this.closeCreateEditModal)
        }

        this.$nextTick(() => {
          this.$refs.form.reset()
        })
      })
    },

    openRemoveConfirmModal(item) {
      this.currentItem = { ...item }
      this.showRemoveConfirmModal = true
    },

    closeRemoveConfirmModal() {
      this.showRemoveConfirmModal = false
      this.resetCurrent()
    },

    remove() {
      this.$emit('remove', this.currentItem, this.closeRemoveConfirmModal)
    }
  }
}
</script>

<style lang="scss" scoped>
.modal-footer {
  display: flex;
  align-items: center;

  & div {
    display: flex;
    justify-content: space-between;
    width: 100%;

    & .right-buttons-wrapper {
      display: flex;
      align-items: center;
      justify-content: flex-end;

      & .app-checkbox {
        margin-right: 5px;
      }
    }
  }
}
</style>
